import { HttpErrorResponse } from "@angular/common/http";
import { SalePointDto } from "../../services/generated/erp-api/models/sale-point-dto";
import { getI18nErrorCodeFromHttpError } from "../../shared/utils/api-i18n-error-code";

export interface SalePointQuestion {
  fr: string;
  en: string;
  ar: string;
}

/**
 * Look of the cards in the app picker: `outline` = white card with a light
 * border, `shadow` = white card with a soft shadow, `flat` = brand-tinted card.
 */
export type SalePointCardStyle = "outline" | "shadow" | "flat";

export const SALE_POINT_CARD_STYLES: readonly SalePointCardStyle[] = ["outline", "shadow", "flat"];

/**
 * `salePoints` block of the vitrine design JSON (`vitrineWhatsAppConfig`).
 * The client always chooses on opening and sees one flat list: the former
 * `choiceMoment` and `groupByCity` keys are neither written nor read any more.
 */
export interface SalePointsSettings {
  enabled: boolean;
  question: SalePointQuestion;
  cardStyle: SalePointCardStyle;
  showAddress: boolean;
  showPhone: boolean;
}

export const DEFAULT_SALE_POINTS_SETTINGS: SalePointsSettings = {
  enabled: false,
  question: {
    fr: "Choisissez votre point de vente",
    en: "Choose your store",
    ar: "اختر نقطة البيع",
  },
  cardStyle: "outline",
  showAddress: true,
  showPhone: false,
};

/** Unknown or legacy values (v1 "simple") fall back to the default style. */
export function normalizeSalePointCardStyle(value: unknown): SalePointCardStyle {
  return (SALE_POINT_CARD_STYLES as readonly unknown[]).includes(value)
    ? (value as SalePointCardStyle)
    : DEFAULT_SALE_POINTS_SETTINGS.cardStyle;
}

/** Value used in the orders filter for "Non attribué". */
export const SALE_POINT_UNASSIGNED = "__UNASSIGNED__";

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

/** Saved block merged over the defaults; anything missing or malformed falls back to its default. */
export function mergeSalePointsSettings(saved: unknown): SalePointsSettings {
  const defaults = structuredClone(DEFAULT_SALE_POINTS_SETTINGS);
  if (!isRecord(saved)) return defaults;
  const question = isRecord(saved["question"]) ? saved["question"] : {};
  const text = (value: unknown, fallback: string) =>
    typeof value === "string" && value.trim() ? value : fallback;
  const flag = (value: unknown, fallback: boolean) =>
    typeof value === "boolean" ? value : fallback;
  return {
    enabled: flag(saved["enabled"], defaults.enabled),
    question: {
      fr: text(question["fr"], defaults.question.fr),
      en: text(question["en"], defaults.question.en),
      ar: text(question["ar"], defaults.question.ar),
    },
    cardStyle: normalizeSalePointCardStyle(saved["cardStyle"]),
    showAddress: flag(saved["showAddress"], defaults.showAddress),
    showPhone: flag(saved["showPhone"], defaults.showPhone),
  };
}

export function sortSalePoints(salePoints: SalePointDto[]): SalePointDto[] {
  return [...salePoints].sort(
    (a, b) =>
      (a.orderPosition ?? Number.MAX_SAFE_INTEGER) -
        (b.orderPosition ?? Number.MAX_SAFE_INTEGER) ||
      (a.name || "").localeCompare(b.name || ""),
  );
}

/** Sale points the clients can pick: active and not archived. */
export function usableSalePoints(salePoints: SalePointDto[]): SalePointDto[] {
  return sortSalePoints(
    salePoints.filter((item) => item.active !== false && !item.archived),
  );
}

/** Adds the reference to the hidden list when absent, removes it when present. */
export function toggleHiddenReference(
  hidden: readonly string[],
  reference: string,
): string[] {
  if (!reference) return [...hidden];
  return hidden.includes(reference)
    ? hidden.filter((item) => item !== reference)
    : [...hidden, reference];
}

/** Forces a set of references to hidden (true) or shown (false), keeping the others untouched. */
export function setHiddenReferences(
  hidden: readonly string[],
  references: readonly string[],
  hide: boolean,
): string[] {
  const targets = new Set(references.filter((reference) => !!reference));
  const kept = hidden.filter((item) => !targets.has(item));
  return hide ? [...kept, ...targets] : kept;
}

/** "Proposé dans" selection for a category/product: every listed sale point that is not excluded. */
export function hiddenToSelectedSalePoints(
  salePointReferences: readonly string[],
  hiddenSalePointReferences: readonly string[] | null | undefined,
): string[] {
  const hidden = new Set(hiddenSalePointReferences || []);
  return salePointReferences.filter((reference) => !hidden.has(reference));
}

/**
 * `hiddenSalePointReferences` to send for a "Proposé dans" selection: the listed
 * sale points left unselected, plus the exclusions already stored for sale points
 * that are not listed (archived ones), so restoring one keeps its previous state.
 */
export function selectedToHiddenSalePoints(
  salePointReferences: readonly string[],
  selectedReferences: readonly string[] | null | undefined,
  previousHidden: readonly string[] | null | undefined = [],
): string[] {
  const listed = new Set(salePointReferences);
  const selected = new Set(selectedReferences || []);
  return [
    ...(previousHidden || []).filter((reference) => !listed.has(reference)),
    ...salePointReferences.filter((reference) => !selected.has(reference)),
  ];
}

/** Orders filter values (sale-point references and/or the "Non attribué" entry) to the `SearchOrders` fields. */
export function buildSalePointOrderFilter(
  values: readonly string[] | null | undefined,
): { salePointReferences?: string[]; salePointUnassigned?: boolean } {
  const selected = values || [];
  const references = selected.filter(
    (value) => !!value && value !== SALE_POINT_UNASSIGNED,
  );
  return {
    ...(references.length ? { salePointReferences: references } : {}),
    ...(selected.includes(SALE_POINT_UNASSIGNED)
      ? { salePointUnassigned: true }
      : {}),
  };
}

/** Inverse of {@link buildSalePointOrderFilter}, used to restore a stored filter. */
export function salePointOrderFilterValues(
  filter: Record<string, unknown> | null | undefined,
): string[] {
  const references = Array.isArray(filter?.["salePointReferences"])
    ? (filter!["salePointReferences"] as unknown[]).filter(
        (value): value is string => typeof value === "string" && !!value,
      )
    : [];
  return filter?.["salePointUnassigned"] === true
    ? [...references, SALE_POINT_UNASSIGNED]
    : references;
}

/** Name of the query parameter that opens a product page on one sale point's stock row. */
export const SALE_POINT_QUERY_PARAM = "salePoint";

/** Reference carried by `?salePoint=<reference>` on the product edit route; null when absent or blank. */
export function readSalePointDeepLink(
  params: { get(name: string): string | null } | null | undefined,
): string | null {
  const value = (params?.get(SALE_POINT_QUERY_PARAM) || "").trim();
  return value || null;
}

/** Route commands and query params of the "Fiche produit" link of the menu editor. */
export function productEditLink(
  productReference: string,
  salePointReference: string | null | undefined,
): { commands: string[]; queryParams: Record<string, string> } {
  return {
    commands: ["/catalog/products", productReference, "edit"],
    queryParams: salePointReference ? { [SALE_POINT_QUERY_PARAM]: salePointReference } : {},
  };
}

/**
 * Stock of a product at a sale point, as the app computes it: the product's own
 * stock flag AND no "out of stock here" row.
 */
export function isInStockAtSalePoint(
  product: { reference?: string; inStock?: boolean | null },
  outOfStockProductReferences: readonly string[],
): boolean {
  return product.inStock !== false && !outOfStockProductReferences.includes(product.reference || "");
}

/** Translate key for a failed sale-point call (HTTP error or an ApiResponse carrying an error code). */
export function salePointErrorKey(
  error: unknown,
  fallbackKey = "SALE_POINT.ERROR.GENERIC",
): string {
  const payload =
    error instanceof HttpErrorResponse ? error.error : (error as any);
  const code = String(
    getI18nErrorCodeFromHttpError(error) || payload?.message || "",
  );
  if (code.includes("SALE_POINT_NAME_EXISTS"))
    return "SALE_POINT.ERROR.NAME_EXISTS";
  if (code.includes("SALE_POINT_NAME_REQUIRED"))
    return "SALE_POINT.ERROR.NAME_REQUIRED";
  if (code.includes("SALE_POINT_NOT_FOUND"))
    return "SALE_POINT.ERROR.NOT_FOUND";
  if (code.includes("SALE_POINT_LAST_STORE"))
    return "SALE_POINT.ERROR.LAST_STORE";
  return fallbackKey;
}

/**
 * The ERP answers either with an HTTP error or with HTTP 200 and a non-"200"
 * `status` in the ApiResponse; both must be treated as a failure.
 */
/**
 * The main sale point of a restaurant that has none yet: its name and address, the courier pickup pin (and its
 * address and contact) when set. Null for other shops, or without a name to give it.
 */
export function mainSalePointOf(
  company: { activity?: string; name?: string; address?: string; city?: string } | null | undefined,
  pickup: {
    pickupAddress?: string | null;
    pickupCity?: string | null;
    pickupLat?: number | null;
    pickupLng?: number | null;
    pickupContactName?: string | null;
    pickupPhone?: string | null;
  } | null | undefined,
): SalePointDto | null {
  const name = company?.name?.trim();
  if (company?.activity !== "RESTAURANT" || !name) return null;
  const pinned = pickup?.pickupLat != null && pickup?.pickupLng != null;
  const main: SalePointDto = {
    name,
    address: pickup?.pickupAddress?.trim() || company.address?.trim() || undefined,
    city: pickup?.pickupCity?.trim() || company.city?.trim() || undefined,
    active: true,
  };
  if (pinned) {
    main.pickupLat = pickup!.pickupLat!;
    main.pickupLng = pickup!.pickupLng!;
  }
  if (pickup?.pickupContactName?.trim()) main.pickupContactName = pickup.pickupContactName.trim();
  if (pickup?.pickupPhone?.trim()) main.pickupContactPhone = pickup.pickupPhone.trim();
  return main;
}

export function isApiResponseOk(
  response: { status?: string } | null | undefined,
): boolean {
  return !!response && (!response.status || response.status === "200");
}
